import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import apiClient from "../../utils/api/apiClient";
import { APIEndpoints } from "../../utils/api/apiConfig";
import { getErrorMessageFromApiResponse } from "../../utils/api/apiUtils";
import { getIsoDate } from "../../utils/getIsoDate.js";
import { cloneDeep } from "lodash";
import useStores from "../useStore";
const AppointmentsAdministrationContext = React.createContext({});
export function useAppointmentsAdministrationState() {
  return React.useContext(AppointmentsAdministrationContext);
}

const AppointmentsAdministrationProvider = ({ children }) => {
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [error, setError] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedCabin, setSelectedCabin] = useState("");
  const [appointmentDetails, setAppointmentDetails] = useState("");
  const [cabinTypes, setCabinTypes] = useState([]);
  const [bookingSlots, setBookingSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [
    showDailyAppointmentsCompareModal,
    setShowDailyAppointmentsCompareModal,
  ] = useState(false);
  const mountedRef = useRef(true);
  const [dates, setDates] = useState([]);
  const { overlayRightStore } = useStores();
  const setShowOverviewOverlay = () =>
    overlayRightStore.openOrClose("dailyAppointmentsOverview");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedMonth, setSelectedMonth] = useState([
    getIsoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
  ]);

  const getPreparedPeriods = useCallback((selectedDays) => {
    if (!Array.isArray(selectedDays) || selectedDays.length === 0) {
      return [];
    }

    // Ensure we're creating precise day periods
    const preparedPeriods = selectedDays.map((day) => {
      const startDate = new Date(day[0]);
      const endDate = new Date(day[1]);

      // Format dates to ensure they're for the specific day only
      return {
        start: `${startDate.toISOString().split("T")[0]}T00:00:00`,
        end: `${endDate.toISOString().split("T")[0]}T23:59:59`,
      };
    });

    return preparedPeriods;
  }, []);

  // get bookingOverview for the selectedDays
  const getBookingOverview = useCallback(
    (selectedDays) => {
      if (selectedDays.length > 0) {
        let periods = getPreparedPeriods(selectedDays);
        apiClient
          .postJson(APIEndpoints.bookingOverview, {
            appointmentTypeId: "00000000-0000-0000-0000-000000000000",
            cabinId: selectedCabin,
            periods: periods,
          })
          .then((response) => {
            let slots = [];
            response.forEach((appType) =>
              appType.cabins[0].bookingSlots.forEach((slot) => {
                slot.appointmentType = appType.appointmentType;
                slot.cabin = appType.cabins[0].cabin;
                slot.type = appType.type;
                slot.isLocked =
                  slot.type === "lock" || slot.status === "isLocked";
                slots.push(slot);
              })
            );
            // Preserve existing slots that aren't in the current response
            const currentDates = slots.map(
              (slot) => slot.startTime.split("T")[0]
            );
            const existingSlots = bookingSlots.filter(
              (slot) => !currentDates.includes(slot.startTime.split("T")[0])
            );
            setBookingSlots([...existingSlots, ...slots]);
          })
          .catch((error) => {
            setError(getErrorMessageFromApiResponse(error));
          });
      }
    },
    [getPreparedPeriods, selectedCabin, bookingSlots]
  );

  useEffect(() => {
    if (selectedDays.length > 0) {
      // Set selectedDate to the last date in the selectedDays array
      let date = selectedDays[selectedDays.length - 1][0].slice(0, 10);
      setSelectedDate(date);
    } else {
      // If no dates are selected, clear the selectedDate
      setSelectedDate("");
    }
  }, [selectedDays]);

  const blockedDates = useMemo(() => {
    // let periods = getPreparedPeriods(selectedDays);
    const lockedSlots = bookingSlots.filter((slot) => slot.isLocked);
    const uniqueBlockedDates = Array.from(
      new Set(
        lockedSlots.map((slot) => new Date(slot.startTime).toDateString())
      )
    ).map((dateString) => new Date(dateString)); // Convert back to Date objects
    return uniqueBlockedDates;
  }, [bookingSlots]);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (selectedCabin) {
      // Create a date object from the selected month
      const startDate = new Date(selectedMonth);

      // Get the end date by moving to the first day of next month and subtracting one day
      const endDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        0
      );

      getBookingOverview([[getIsoDate(startDate), getIsoDate(endDate)]]);
    }
  }, [selectedMonth, selectedCabin]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (dates) {
      let sortedDates = cloneDeep(dates).sort((a, b) => (a > b ? 1 : -1));
      let newPeriods = [];
      for (let date of sortedDates) {
        newPeriods.push([getIsoDate(date), getIsoDate(date, true)]);
      }
      setSelectedDays(newPeriods);
    }
  }, [dates]);

  //get AppointmentTypes from Backend

  useEffect(() => {
    setError("");
    apiClient
      .getJson(APIEndpoints.appointmenttypes())
      .then((response) => {
        if (!mountedRef.current) {
          return null;
        }
        setAppointmentTypes(response);
      })
      .catch((error) => {
        setError(getErrorMessageFromApiResponse(error));
      });
    apiClient
      .getJson(APIEndpoints.cabins())
      .then((response) => {
        if (!mountedRef.current) {
          return null;
        }
        setCabinTypes(response);
        setSelectedDate([]);
      })
      .catch((error) => {
        setError(getErrorMessageFromApiResponse(error));
      });
  }, []);

  //Cleanup Function
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // removes the canceled event from the bookingSlots array (so it doesn't show in the calendar)
  const removeCanceledEvent = useCallback(() => {
    let filteredBookingSlots = bookingSlots.filter(
      (slot) => slot.id !== appointmentDetails.id
    );
    setBookingSlots(filteredBookingSlots);
    overlayRightStore.closeAll();
    setAppointmentDetails("");
  }, [appointmentDetails.id, bookingSlots, overlayRightStore]);

  const lockAll = (comment = "") => {
    setError("");
    setSuccessMessage(""); // Clear previous messages

    const startDate = new Date(selectedMonth);

    // Get the end date by moving to the first day of next month and subtracting one day
    const endDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    );
    const preparedParams = {
      periods: getPreparedPeriods(selectedDays),
      comment: comment,
    };
    apiClient
      .postJson(APIEndpoints.cabins(selectedCabin).locks, preparedParams)
      .then((result) => {
        getBookingOverview([[getIsoDate(startDate), getIsoDate(endDate)]]);
      })
      .catch((error) => {
        setError(getErrorMessageFromApiResponse(error));
      });
  };

  // const deleteEvent = (event) => {
  //     console.log('Selected Days:', selectedDays)
  //     const preparedParams = { periods: getPreparedPeriods(selectedDays), [event]: true }
  //     return apiClient.deleteJson(APIEndpoints.cabins(selectedCabin).events, null, true, true, { data: preparedParams })
  // }

  const deleteEvent = async (event) => {
    setErrorMessage("");
    setSuccessMessage("");
    try {
      // Get only the selected days for unlocking
      const selectedPeriods = getPreparedPeriods(selectedDays);

      const preparedParams = {
        periods: selectedPeriods,
        [event]: true,
      };

      await apiClient.deleteJson(
        APIEndpoints.cabins(selectedCabin).events,
        null,
        true,
        true,
        { data: preparedParams }
      );

      // Get the current month's first and last day
      const startDate = new Date(selectedMonth);
      const endDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        0
      );

      // Refresh the entire month view to ensure proper state
      const monthPeriod = [[getIsoDate(startDate), getIsoDate(endDate)]];
      getBookingOverview(monthPeriod);
    } catch (error) {
      setErrorMessage(getErrorMessageFromApiResponse(error));
    }
  };

  const isStep1Done = useMemo(() => {
    if (selectedCabin !== "") return true;
    else return false;
  }, [selectedCabin]);

  const isStep2Done = useMemo(() => {
    if (selectedDays.length) return true;
    else return false;
  }, [selectedDays]);

  const contextValues = {
    dates,
    setDates,
    isStep1Done,
    isStep2Done,
    appointmentTypes,
    selectedDays,
    setSelectedDays,
    selectedCabin,
    setSelectedCabin,
    cabinTypes,
    deleteEvent,
    lockAll,
    error,
    appointmentDetails,
    setAppointmentDetails,
    bookingSlots,
    setBookingSlots,
    selectedDate,
    setSelectedDate,
    showDailyAppointmentsCompareModal,
    setShowDailyAppointmentsCompareModal,
    setShowOverviewOverlay,
    removeCanceledEvent,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    blockedDates,
    selectedMonth,
    setSelectedMonth,
    getBookingOverview,
  };
  return (
    <AppointmentsAdministrationContext.Provider value={contextValues}>
      {children}
    </AppointmentsAdministrationContext.Provider>
  );
};

export default AppointmentsAdministrationProvider;
